import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { TextBubblesIcon } from "~/common/components/icons/TextBubblesIcon";
import {
  EmptyTableState,
  Table,
  TableData,
  TableHead,
} from "~/common/components/table";
import { Patient } from "~/common/utils";
import { PATIENT_FILTER_PARAMS } from "../constants/patients";
import { ROUTES } from "../router";
import { defaultHomeParams, PATIENTS_FILTERS } from "../utils";
import { LoadingPatientsTable } from "./LoadingPatientsTable";
import { patientsTableColumns } from "./patientsTable/patientsTableColumns";
import { SortableTableHeader } from "./patientsTable/SortableTableHeader";
import { usePatientsTableSorting } from "./patientsTable/usePatientsTableSorting";

interface PatientsTableProps {
  patients?: Patient[];
  isLoading?: boolean;
  isFetching?: boolean;
}

const PatientsTable = ({
  patients,
  isLoading = false,
  isFetching = false,
}: PatientsTableProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(defaultHomeParams);
  const { sorting, onSortingChange } = usePatientsTableSorting();

  const selectedTab = searchParams.get(PATIENT_FILTER_PARAMS.filter);
  const isAllFilter = selectedTab === PATIENTS_FILTERS.all;
  const noPatients = !patients?.length;

  const table = useReactTable({
    data: patients ?? [],
    columns: patientsTableColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    onSortingChange,
    state: { sorting },
  });

  const handleRowClick = (patientId: string) =>
    navigate(
      generatePath(ROUTES.activePatientActivityFeed, {
        patientId,
      }),
    );

  return (
    <div
      className={twMerge(
        "overflow-y-auto rounded-2xl border border-gray-200",
        noPatients && "h-table overflow-clip",
      )}
    >
      <Table className="table-auto">
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <SortableTableHeader
                  key={header.id}
                  header={header}
                  className={twMerge(
                    index === 0 && "w-1/4 rounded-ss-2xl",
                    index === headerGroup.headers.length - 1 &&
                      "rounded-se-2xl",
                    index > 0 &&
                      index < headerGroup.headers.length - 1 &&
                      "flex-1",
                  )}
                />
              ))}
            </tr>
          ))}
        </TableHead>
        {isLoading ? (
          <LoadingPatientsTable />
        ) : (
          <tbody className="max-h-[38rem] text-sm text-gray-70">
            {noPatients ? (
              <EmptyTableState
                colSpan={patientsTableColumns.length}
                message={
                  isAllFilter ? "No patients found" : "No unreviewed patients"
                }
                icon={!isAllFilter ? <TextBubblesIcon /> : null}
              />
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => handleRowClick(row.original.id)}
                  className={twMerge(
                    "transition hover:cursor-pointer hover:bg-gray-50",
                    isFetching && "opacity-75",
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableData key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableData>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        )}
      </Table>
    </div>
  );
};

export default PatientsTable;
